/* eslint-disable @angular-eslint/no-empty-lifecycle-method */

import {throwError as observableThrowError,  Subject, timer } from 'rxjs';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, Injector, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { AutoUnsubscribe } from 'ngx-auto-unsubscribe';
import { Observable } from 'rxjs-compat/Observable';
import { catchError, exhaustMap, first } from 'rxjs/operators';
import { AppState } from '../../interfaces/app-state.interface';
import { RoutesTable } from '../../resources/routes-table.model';
import { UsersAction } from '../../state-management/users/users.actions';
import { LoginWraperService } from '../login.wraper.service';
import { InternetService } from './../internet/internet.service';
import { SettingsService } from './../settings/settings.service';

const unauthorizedErrorCode = 401;
const trustedDomainRoutesWhiteList = [
    RoutesTable.authentication.routes.login.uri,
    RoutesTable.authentication.routes.logout.uri,
    RoutesTable.authentication.routes.getSessionID.uri,
    RoutesTable.environment.routes.getEnvironmentConfig.uri,
    'https://www.googleapis.com/youtube/v3/search',
    'https://www.googleapis.com/youtube/v3/search?part=snippet&channelId=UCKYI3oKkE36sJl3vnL9DV-A&eventType=live&type=video&key=AIzaSyDrkogmCn-RnoR3Ou3D4k5gv9xR3l_8zo4',
];

@AutoUnsubscribe()
@Injectable()
export class UnauthorizedErrorHandlerService implements HttpInterceptor, OnDestroy {

    public forceUserAuthentication$: Observable<any>;

    private canRetryLogin: boolean = true;
    private internetService: InternetService;
    private requestGetOkSource: Subject<any> = new Subject<any>();
    private loginWraperService: LoginWraperService;
    private loginAttemptInProgress: boolean;

    constructor(
        private store: Store<AppState>,
        private injector: Injector,
        private settingsService: SettingsService,
    ) {

        this.forceUserAuthentication$ = this.requestGetOkSource.asObservable();
        timer(1)
            .pipe(
              first())
            .subscribe(() => {

                this.internetService = this.injector.get(InternetService);
                this.settingsService = this.injector.get(SettingsService);
                const settings = this.settingsService.getConfigurationKeyValue('csf') || {};

                if (this.settingsService.getConfigurationKeyValue('csf')) {
                    trustedDomainRoutesWhiteList.push(
                        settings.urlSessionId,
                        settings.urlChatRequest,
                        settings.urlGetMessages,
                        settings.urlSendMessages,
                        settings.urlChatEnd,
                    );
                }

            });
    }

    public ngOnDestroy() {
        // Não remover, necessário para autoUnsubscribe
    }

    /**
     * Intercept HTTP requests
     *
     * @param request request
     * @param next
     */
    public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

        if (!this.isTrustedDomainEnvironment(request.url) || this.whitelistContains(request.url)) {
            return next.handle(request);
        }

        return next
            .handle(request)
            .pipe(
              catchError((error: HttpErrorResponse) => {

                if (error instanceof HttpErrorResponse && error.status === unauthorizedErrorCode && this.canRetryLogin) {

                    // Não permite mais a retentativa de login
                    this.canRetryLogin = false;

                    return this.tryAuthenticateUser(request.url)
                    .pipe(
                        exhaustMap((newToken) => {

                            const headers = request.headers.set('Authorization', `Bearer ${newToken}`);
                            const duplicate = request.clone({ headers });

                            return this.intercept(duplicate, next);

                        }),
                        catchError(() => {

                            this.loginAttemptInProgress = false;
                            // tslint:disable-next-line:no-console
                            console.warn(`[App Lifecycle Info] Unespected erro handling 401 error code`);

                            return observableThrowError(error);

                        }));

                }

                throw error;

        }));

    }

    /**
     * Verifica se uma URL está presente na whitelist
     *
     * @param uri endereço 'relativo' do recurso
     */
    private whitelistContains(uri: string) {
        return trustedDomainRoutesWhiteList.find((listedUri) => uri.match(new RegExp(`${listedUri}`, `gi`)) != null ) != null;
    }

    /**
     * Tentar logar o usuário
     *
     * @param url url de origem do erro
     */
    private tryAuthenticateUser(url: string): Observable<any> {

        // User login in progress. Wait for the end
        if (this.loginAttemptInProgress) {
            return new Observable((observer) => this.forceUserAuthentication$.subscribe((data) => {

                data ? observer.next(data) : observer.error(data);
                observer.complete();

            }));
        } else {

            // Login in progress flag
            this.loginAttemptInProgress = true;
            if (!this.loginWraperService) this.loginWraperService = this.injector.get(LoginWraperService);

            return new Observable(() => {

                this.store.dispatch({type: UsersAction.logout});

                if (this.settingsService.isOnLoggedDomain()) {
                    this.loginWraperService.navigateToLogin();
                }

            });
        }

    }

    /**
     * Verificar se a URL requisitada pertence as APIs da toro
     *
     * @param url url to validate
     */
    private isTrustedDomainEnvironment(url: string) {

        const { backend } = this.settingsService.getSettingsSnapshot();

        if (!backend) return false;

        return Object
            .values(backend)
            .some((baseUrl: string) => url.includes(baseUrl));

    }

}
